import { useEffect, useRef, useState } from 'react';
import { GripVertical, Plus, Pencil, Trash2, ArrowUp, ArrowDown, Pin } from 'lucide-react';
import { api, type Block } from '../api.js';
import { Screen, Section, Field, Seg, Btn, Sheet, Badge, Toggle, EmptyState } from '../ui/kit.js';
import { reporter } from '../pwa/report.js';

// The blocks manager: every prompt piece Niklas has written, in the order
// they ride, draggable by the handle (one pointer path serves mouse and
// touch), with arrows for precision. "Always" blocks ride every job; a pin
// moves a block to the very beginning or the very end of the assembled
// prompt. The strip below shows the true final order.

export function BlocksPage() {
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [editing, setEditing] = useState<Block | 'new' | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const load = () => api.get<{ templates: Block[] }>('/api/templates').then((r) => setBlocks(r.templates));
  useEffect(() => {
    void load();
  }, []);

  const commitOrder = (next: Block[]) => {
    setBlocks(next);
    // A reorder that fails silently appears to work and then reverts on the
    // next load, which is worse than not reordering (V6 section J).
    void api.post('/api/templates/reorder', { order: next.map((b) => b.id) }).catch(reporter('the new order'));
  };

  const move = (id: string, delta: number) => {
    const idx = blocks.findIndex((b) => b.id === id);
    const to = idx + delta;
    if (idx < 0 || to < 0 || to >= blocks.length) return;
    const next = [...blocks];
    const [row] = next.splice(idx, 1);
    next.splice(to, 0, row);
    commitOrder(next);
  };

  // Pointer drag: the handle captures the pointer; each row the pointer
  // crosses swaps with the dragged one; release commits.
  const onHandleDown = (e: React.PointerEvent, id: string) => {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    setDragId(id);
  };
  const onHandleMove = (e: React.PointerEvent) => {
    if (!dragId || !listRef.current) return;
    const rows = Array.from(listRef.current.querySelectorAll<HTMLElement>('[data-block-id]'));
    const over = rows.find((r) => {
      const box = r.getBoundingClientRect();
      return e.clientY >= box.top && e.clientY <= box.bottom;
    });
    const overId = over?.dataset.blockId;
    if (!overId || overId === dragId) return;
    const from = blocks.findIndex((b) => b.id === dragId);
    const to = blocks.findIndex((b) => b.id === overId);
    if (from < 0 || to < 0) return;
    const next = [...blocks];
    const [row] = next.splice(from, 1);
    next.splice(to, 0, row);
    setBlocks(next);
  };
  const onHandleUp = () => {
    if (dragId) commitOrder(blocks);
    setDragId(null);
  };

  const finalOrder = [
    ...blocks.filter((b) => b.pin === 'begin').map((b) => b.title),
    ...blocks.filter((b) => !b.pin).map((b) => b.title),
    'Your job text',
    'The standing contract',
    ...blocks.filter((b) => b.pin === 'end').map((b) => b.title),
  ];

  return (
    <Screen
      eyebrow="More"
      title="Blocks"
      sub="The prompt pieces every job can carry. Drag to reorder; pins go first or last."
      actions={
        <Btn kind="primary" onClick={() => setEditing('new')}>
          <Plus size={15} /> New
        </Btn>
      }
    >
      {blocks.length === 0 ? (
        <EmptyState title="No blocks yet">
          Write the instructions you find yourself repeating: verification habits, style rules, standing context.
          Each becomes a chip in Compose.
        </EmptyState>
      ) : (
        <div className="ops-rows" ref={listRef}>
          {blocks.map((b) => (
            <div
              key={b.id}
              data-block-id={b.id}
              className={`ops-row ops-blockrow${dragId === b.id ? ' ops-blockrow--drag' : ''}`}
            >
              <button
                type="button"
                className="ops-blockrow__handle"
                aria-label={`Reorder ${b.title}`}
                onPointerDown={(e) => onHandleDown(e, b.id)}
                onPointerMove={onHandleMove}
                onPointerUp={onHandleUp}
                onPointerCancel={onHandleUp}
              >
                <GripVertical size={16} />
              </button>
              <button type="button" className="ops-row__main" style={{ textAlign: 'left' }} onClick={() => setEditing(b)}>
                <div className="ops-row__title">
                  {b.title}
                  {b.always === 1 ? <Badge tone="accent">always</Badge> : null}
                  {b.pin ? <Badge tone="wait">{b.pin === 'begin' ? 'first' : 'last'}</Badge> : null}
                </div>
                <div className="ops-row__sub">{b.text.slice(0, 90)}</div>
              </button>
              <div className="ops-row__end ops-blockrow__tools">
                <button type="button" aria-label={`Move ${b.title} up`} onClick={() => move(b.id, -1)}>
                  <ArrowUp size={15} />
                </button>
                <button type="button" aria-label={`Move ${b.title} down`} onClick={() => move(b.id, 1)}>
                  <ArrowDown size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {blocks.length ? (
        <Section title="How the prompt assembles">
          <div className="ops-order">
            {finalOrder.map((t, i) => (
              <span key={`${t}-${i}`} className={`ops-order__item${t === 'Your job text' || t === 'The standing contract' ? ' ops-order__item--fixed' : ''}`}>
                {i + 1}. {t}
              </span>
            ))}
          </div>
          <p className="ops-hint">
            The machine header always opens the prompt. Compose shows the exact assembled text live.
          </p>
        </Section>
      ) : null}

      {editing ? (
        <BlockEditor
          block={editing === 'new' ? null : editing}
          onClose={() => {
            setEditing(null);
            void load();
          }}
        />
      ) : null}
    </Screen>
  );
}

function BlockEditor(props: { block: Block | null; onClose: () => void }) {
  const [title, setTitle] = useState(props.block?.title ?? '');
  const [text, setText] = useState(props.block?.text ?? '');
  const [always, setAlways] = useState(props.block?.always === 1);
  const [pin, setPin] = useState<'none' | 'begin' | 'end'>(props.block?.pin ?? 'none');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const body = { title, text, always, pin: pin === 'none' ? null : pin };
      if (props.block) await api.patch(`/api/templates/${props.block.id}`, body);
      else await api.post('/api/templates', body);
      props.onClose();
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!props.block) return;
    setBusy(true);
    await api.del(`/api/templates/${props.block.id}`);
    props.onClose();
  };

  return (
    <Sheet title={props.block ? 'Edit block' : 'New block'} onClose={props.onClose}>
      <Field label="Title">
        <input className="ops-input" value={title} onChange={(e) => setTitle(e.target.value)} />
      </Field>
      <div style={{ height: 12 }} />
      <Field label="Instructions" hint="Included in the session prompt exactly as written.">
        <textarea className="ops-textarea" value={text} onChange={(e) => setText(e.target.value)} />
      </Field>
      <div className="ops-blockeditor__row">
        <span>Include in every job</span>
        <Toggle checked={always} onChange={setAlways} label="Include in every job" />
      </div>
      <Field label="Position in the prompt">
        <Seg
          value={pin}
          onChange={setPin}
          options={[
            { value: 'none', label: 'In order' },
            {
              value: 'begin',
              label: (
                <span>
                  <Pin size={12} style={{ verticalAlign: '-1px', marginRight: 4 }} />
                  First
                </span>
              ),
            },
            {
              value: 'end',
              label: (
                <span>
                  <Pin size={12} style={{ verticalAlign: '-1px', marginRight: 4 }} />
                  Last
                </span>
              ),
            },
          ]}
        />
      </Field>
      <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
        <Btn kind="primary" big disabled={busy || !title.trim() || !text.trim()} onClick={() => void save()}>
          <Pencil size={15} /> Save block
        </Btn>
        {props.block ? (
          <Btn kind="danger" disabled={busy} onClick={() => void remove()}>
            <Trash2 size={15} />
          </Btn>
        ) : null}
      </div>
    </Sheet>
  );
}
